import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { serverStatusQueryOptions } from "@/lib/client/apex";
import { cn } from "@/lib/utils";
import { DefaultImageCard } from "@/features/home/components/image-card";
import { Loading } from "@/features/shared/components/loading";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const StatusColorMap = {
  UP: "text-green-400",
  SLOW: "text-yellow-400",
  DOWN: "text-red-400",
  UNKNOWN: "text-gray-400",
};

const StatusIconMap = {
  UP: "🟢",
  SLOW: "🟡",
  DOWN: "🔴",
  UNKNOWN: "⚫",
};

const RegionMap = {
  "US-West": "美西",
  "US-Central": "美中",
  "US-East": "美东",
  "EU-West": "欧西",
  "EU-East": "欧东",
  Asia: "亚洲",
  SouthAmerica: "南美",
};

const ServiceMap = {
  EA_novafusion: "EA",
  ApexOauth_Crossplay: "跨平台",
  // selfCoreTest: '核心',
};

export function ServerStatus() {
  const [selectedService, setSelectedService] = useState<keyof typeof ServiceMap>("EA_novafusion");
  const { data, isLoading } = useQuery(serverStatusQueryOptions);

  const currentServiceData = useMemo(() => {
    return data?.[selectedService];
  }, [data, selectedService]);

  const overallStatus = useMemo(() => {
    if (!data) return "UNKNOWN";

    let downCount = 0;
    let slowCount = 0;
    let totalCount = 0;

    // 检查主要服务状态
    Object.values(ServiceMap).forEach((_, serviceKey) => {
      const service = Object.keys(ServiceMap)[serviceKey] as keyof typeof ServiceMap;
      const serviceData = data[service];
      if (serviceData) {
        Object.values(serviceData).forEach((server) => {
          if ("Status" in server) {
            totalCount++;
            if (server.Status === "DOWN") downCount++;
            else if (server.Status === "SLOW") slowCount++;
          }
        });
      }
    });

    if (downCount > 0) return "DOWN";
    if (slowCount > totalCount * 0.3) return "SLOW";
    return "UP";
  }, [data]);

  if (!data || isLoading || !currentServiceData) return <Loading size={4} className="mx-auto" />;

  return (
    <DefaultImageCard image="/images/home/server.webp">
      <div className="flex flex-col gap-2 text-end text-white text-shadow-md">
        <Tabs
          defaultValue={selectedService}
          className="-mt-3 self-end"
          onValueChange={(value) => setSelectedService(value as keyof typeof ServiceMap)}
        >
          <TabsList className="grid grid-cols-2 bg-transparent p-0 justify-self-end text-xs space-x-2">
            {Object.entries(ServiceMap).map(([key, name]) => (
              <TabsTrigger
                key={key}
                value={key}
                className="px-1 py-1 text-xs text-white data-[state=active]:text-foreground"
              >
                {name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <h1 className="text-xl font-bold">
          <span className="text-xs text-white/60 font-mono mr-2">服务器状态</span>
          <span className={cn(StatusColorMap[overallStatus], "font-mono")}>{overallStatus}</span>
        </h1>

        <div className="text-sm font-mono space-y-1 grid grid-cols-2 gap-x-4 gap-y-1">
          {Object.entries(currentServiceData)
            .filter(([_, status]) => "Status" in status)
            .map(([region, status]) => {
              return (
                <div key={region} className="flex justify-between items-center space-x-2">
                  <span className="text-white/80">
                    {RegionMap[region as keyof typeof RegionMap] || region}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-xs">
                      {status.ResponseTime}
                      ms
                    </span>
                    <span className={StatusColorMap[status.Status as keyof typeof StatusColorMap]}>
                      {StatusIconMap[status.Status as keyof typeof StatusIconMap]}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </DefaultImageCard>
  );
}
